import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, tap } from 'rxjs';
import { GungBase } from '../../models';

export interface CustomerRequest extends GungBase {
  companyName: string;
  companyOrganisationId: string;
  deliveryPostalCode: string;
  deliveryCity: string;
  deliveryCountry: string;
  billingCompanyName: string;
  billingStreet: string;
  billingPostalCode: string;
  billingCity: string;
  billingCountry: string;
  contactPerson: string;
  phone: string;
  email: string;
  invoiceEmail: string;
  website?: string;
  sameAsDelivery: boolean;
  extra: {
    companyAddress?: string;
    additionalInfo?: string;
    comment?: string;
    [key: string]: any;
  };
  creationDate: Date;
  processed: boolean;
  processedByUserId: string;
  rejected: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CustomerRequestService {
  protected baseUrl = 'json/customer-requests';
  protected http = inject(HttpClient);

  private customerRequestSubject: BehaviorSubject<CustomerRequest[]>;

  public getCustomerRequests(nocache?: boolean): Observable<CustomerRequest[]> {
    const url = this.baseUrl;
    const headers = { maxAge: nocache ? '-1' : '300' };
    return this.http.get<CustomerRequest[]>(url, { headers });
  }

  public markAsProcessed(id: string): Observable<CustomerRequest[]> {
    const url = `${this.baseUrl}/${id}/mark-as-processed`;
    return this.http.get<CustomerRequest[]>(url).pipe(tap(_ => this.updateCustomerRequestSubject(true)));
  }

  public markAsRejected(id: string): Observable<void> {
    const url = `${this.baseUrl}/${id}/mark-as-rejected`;
    return this.http.get<void>(url).pipe(tap(_ => this.updateCustomerRequestSubject(true)));
  }

  public markAsPending(id: string): Observable<void> {
    const url = `${this.baseUrl}/${id}/mark-as-pending`;
    return this.http.get<void>(url).pipe(tap(_ => this.updateCustomerRequestSubject(true)));
  }

  /**
   * Save the admin's free-text note on the request. An empty string clears it; the backend removes
   * the extra key entirely so `extra.comment` stays falsy when there is no comment.
   */
  public saveComment(id: string, comment: string): Observable<void> {
    const url = `${this.baseUrl}/${id}/comment`;
    return this.http.post<void>(url, { comment }).pipe(tap(_ => this.updateCustomerRequestSubject(true)));
  }

  public getCustomerRequestsInternal(nocache?: boolean): Observable<CustomerRequest[]> {
    if (!this.customerRequestSubject) {
      this.customerRequestSubject = new BehaviorSubject<CustomerRequest[]>([]);
      this.updateCustomerRequestSubject(nocache);
    }
    return this.customerRequestSubject.asObservable();
  }

  updateCustomerRequestSubject(nocache?: boolean): void {
    if (!this.customerRequestSubject) {
      return;
    }
    this.getCustomerRequests(nocache)
      .pipe(first())
      .subscribe(data => this.customerRequestSubject.next(data));
  }
}
